/* eslint-disable @ngrx/prefer-effect-callback-in-block-statement */
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import {
  catchError,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { BomExportStatus } from '@cdba/user-interaction/model/feature/bom-export';
import { BOM_EXPORT_RUNNING } from '@cdba/user-interaction/model/feature/bom-export/bom-export-status-enum.model';
import { InteractionType } from '@cdba/user-interaction/model/interaction-type.enum';
import { UserInteractionService } from '@cdba/user-interaction/service/user-interaction.service';

import {
  loadInitialBomExportStatus,
  loadInitialBomExportStatusFailure,
  loadInitialBomExportStatusSuccess,
  requestBomExportFailure,
  requestBomExportSuccess,
  showSnackBar,
  trackBomExportStatus,
  trackBomExportStatusCompleted,
  trackBomExportStatusFailure,
} from '../../actions';
import { BomExportFeature } from '../../reducers/user-interaction/user-interaction.reducer';
import { getBomExportFeature } from '../../selectors';

/**
 * Effects class for all effects which trigger interaction with the user
 */
@Injectable()
export class UserInteractionEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly userInteractionService = inject(UserInteractionService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);

  // Section: User interaction without SnackBar

  // Section: General user interaction by only showing a SnackBar
  showSnackBar$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(showSnackBar),
        tap((action) => {
          this.userInteractionService.interact(action.interactionType);
        })
      ),
    { dispatch: false }
  );

  requestBomExportFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestBomExportFailure),
      map((action) =>
        action.statusCode === HttpStatusCode.BadRequest
          ? showSnackBar({
              interactionType:
                InteractionType.REQUEST_BOM_EXPORT_VALIDATION_ERROR,
            })
          : showSnackBar({
              interactionType: InteractionType.REQUEST_BOM_EXPORT_FAILURE,
            })
      )
    )
  );

  trackBomExportStatusCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(trackBomExportStatusCompleted),
      map(() =>
        showSnackBar({
          interactionType: InteractionType.TRACK_BOM_EXPORT_PROGRESS_COMPLETED,
        })
      )
    )
  );

  trackBomExportStatusFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(trackBomExportStatusFailure),
      map(() =>
        showSnackBar({
          interactionType: InteractionType.TRACK_BOM_EXPORT_PROGRESS_FAILURE,
        })
      )
    )
  );

  // Section: specific use cases where additional actions should be dispatched
  requestBomExportSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestBomExportSuccess),
      mergeMap(() => [
        showSnackBar({
          interactionType: InteractionType.REQUEST_BOM_EXPORT_SUCCESS,
        }),
        trackBomExportStatus(),
      ]),
      catchError((error: Error) =>
        of(trackBomExportStatusFailure({ errorMessage: error.message }))
      )
    )
  );

  loadInitialBomExportStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadInitialBomExportStatus),
      switchMap(() =>
        this.msalBroadcastService.inProgress$.pipe(
          filter(
            (status: InteractionStatus) => status === InteractionStatus.None
          ),
          take(1)
        )
      ),
      switchMap(() =>
        this.store.select(getBomExportFeature).pipe(
          filter((feature: BomExportFeature) => !feature.status),
          take(1)
        )
      ),
      mergeMap(() =>
        this.userInteractionService.loadInitialBomExportStatus().pipe(
          mergeMap((status: BomExportStatus) =>
            BOM_EXPORT_RUNNING.includes(status.progress)
              ? [
                  loadInitialBomExportStatusSuccess({ status }),
                  trackBomExportStatus(),
                ]
              : [loadInitialBomExportStatusSuccess({ status })]
          ),
          catchError((error: HttpErrorResponse) =>
            // Ignore Http404 - it means that the user never requested bom export
            error.status === HttpStatusCode.NotFound
              ? of(loadInitialBomExportStatusSuccess({ status: undefined }))
              : of(
                  showSnackBar({
                    interactionType:
                      InteractionType.GET_BOM_EXPORT_STATUS_FAILURE,
                  }),
                  loadInitialBomExportStatusFailure({
                    errorMessage: error.message,
                  })
                )
          )
        )
      )
    )
  );

  ngrxOnInitEffects(): Action {
    return loadInitialBomExportStatus();
  }
}
